import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getAllMembershipRequests,
  getAllProfiles,
  setUserRole,
  updateMembershipStatus,
} from '../services/membershipService';
import { MembershipRequest, UserProfile, UserRole } from '../types/profile';
import { supabase } from '../lib/supabaseClient';
import { updateNewsPost } from '../services/newsService';
import {
  fetchNewsArticles,
  createNewsArticle,
  updateNewsArticle,
  deleteNewsArticle,
  uploadNewsArticleImage,
} from '../services/newsArticleService';
import type { NewsArticle } from '../types/newsArticle';

type Tab = 'requests' | 'users' | 'posts' | 'news';

interface BlogPost {
  id: string;
  title: string;
  content: string;
  author_id: string;
  author_display_name?: string;
  image_urls: string[];
  created_at: string;
  updated_at?: string;
}

const ROLE_LABELS: Record<UserRole, string> = {
  user: 'Bruker',
  smugler: 'Smugler',
  admin: 'Admin',
};

const ROLE_COLORS: Record<UserRole, string> = {
  user: 'text-gray-400 bg-gray-800',
  smugler: 'text-neon-green bg-emerald-950',
  admin: 'text-yellow-300 bg-yellow-950',
};

const Admin: React.FC = () => {
  const { user, refreshRole } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('requests');

  // Membership requests
  const [requests, setRequests] = useState<
    (MembershipRequest & { profiles: { username: string; avatar_url: string | null } })[]
  >([]);
  const [requestsLoading, setRequestsLoading] = useState(true);

  // Users
  const [profiles, setProfiles] = useState<UserProfile[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);

  // Blog posts
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [editingBlogId, setEditingBlogId] = useState<string | null>(null);
  const [blogTitle, setBlogTitle] = useState('');
  const [blogContent, setBlogContent] = useState('');
  const [blogSubmitting, setBlogSubmitting] = useState(false);
  const [blogSubmitError, setBlogSubmitError] = useState('');

  // News articles
  const [newsArticles, setNewsArticles] = useState<NewsArticle[]>([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [showNewsForm, setShowNewsForm] = useState(false);
  const [editingNewsId, setEditingNewsId] = useState<string | null>(null);
  const [newsTitle, setNewsTitle] = useState('');
  const [newsContent, setNewsContent] = useState('');
  const [newsAuthor, setNewsAuthor] = useState('');
  const [newsImageFile, setNewsImageFile] = useState<File | null>(null);
  const [newsImagePreview, setNewsImagePreview] = useState<string | null>(null);
  const [existingNewsImageUrl, setExistingNewsImageUrl] = useState<string | null>(null);
  const [newsSubmitting, setNewsSubmitting] = useState(false);
  const [newsSubmitError, setNewsSubmitError] = useState('');
  const newsFileRef = useRef<HTMLInputElement>(null);

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [roleError, setRoleError] = useState<string | null>(null);
  const [notice, setNotice] = useState('');

  // ── Load data ──────────────────────────────────────────────────────────────
  const loadRequests = async () => {
    setRequestsLoading(true);
    const { data, error } = await getAllMembershipRequests();
    if (error) console.error('loadRequests error:', error);
    setRequests((data as typeof requests) ?? []);
    setRequestsLoading(false);
  };

  const loadUsers = async () => {
    setUsersLoading(true);
    const { data } = await getAllProfiles();
    setProfiles(data ?? []);
    setUsersLoading(false);
  };

  const loadPosts = async () => {
    setPostsLoading(true);
    const { data } = await supabase
      .from('blog')
      .select('id, title, content, image_urls, author_id, created_at, updated_at')
      .order('created_at', { ascending: false });
    const rows = (data ?? []) as Record<string, unknown>[];
    // Resolve author usernames
    const authorIds = Array.from(new Set(rows.map((r) => r.author_id as string)));
    let nameMap = new Map<string, string>();
    if (authorIds.length > 0) {
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, username')
        .in('user_id', authorIds);
      for (const p of profiles ?? []) nameMap.set(p.user_id as string, p.username as string);
    }
    setPosts(
      rows.map((r) => ({
        id: r.id as string,
        title: r.title as string,
        content: r.content as string,
        author_id: r.author_id as string,
        author_display_name: nameMap.get(r.author_id as string),
        image_urls: Array.isArray(r.image_urls) ? (r.image_urls as string[]) : [],
        created_at: r.created_at as string,
        updated_at: r.updated_at as string | undefined,
      }))
    );
    setPostsLoading(false);
  };

  const loadNewsArticles = async () => {
    setNewsLoading(true);
    const { data } = await fetchNewsArticles(0, 100);
    setNewsArticles(data);
    setNewsLoading(false);
  };

  useEffect(() => {
    loadRequests();
    loadUsers();
    loadPosts();
    loadNewsArticles();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleApprove = async (req: typeof requests[0]) => {
    if (!user) return;
    setActionLoading(req.id);
    await updateMembershipStatus(req.id, 'approved', user.id);
    setNotice(`${req.profiles.username} er nå Smugler!`);
    await loadRequests();
    await loadUsers();
    await refreshRole();
    setActionLoading(null);
  };

  const handleDeny = async (req: typeof requests[0]) => {
    if (!user) return;
    setActionLoading(req.id + '_deny');
    await updateMembershipStatus(req.id, 'denied', user.id);
    setNotice(`Søknad fra ${req.profiles.username} ble avslått.`);
    await loadRequests();
    setActionLoading(null);
  };

  const handleRoleChange = async (profile: UserProfile, newRole: UserRole) => {
    if (newRole === profile.role) return;
    setActionLoading(profile.user_id);
    setRoleError(null);
    const { error } = await setUserRole(profile.user_id, newRole);
    if (error) {
      setRoleError(`Kunne ikke oppdatere rolle for ${profile.username}: ${error.message}`);
    } else {
      setNotice(`${profile.username} sin rolle er oppdatert til ${ROLE_LABELS[newRole]}.`);
    }
    await loadUsers();
    setActionLoading(null);
  };

  const handleDeletePost = async (postId: string) => {
    if (!window.confirm('Slett dette innlegget permanent?')) return;
    setActionLoading(postId);
    await supabase.from('blog').delete().eq('id', postId);
    setNotice('Innlegget er slettet.');
    await loadPosts();
    setActionLoading(null);
  };

  const startEditBlog = (post: BlogPost) => {
    setEditingBlogId(post.id);
    setBlogTitle(post.title);
    setBlogContent(post.content);
    setBlogSubmitError('');
  };

  const resetBlogEdit = () => {
    setEditingBlogId(null);
    setBlogTitle('');
    setBlogContent('');
    setBlogSubmitError('');
  };

  const handleBlogEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingBlogId) return;
    setBlogSubmitting(true);
    setBlogSubmitError('');
    const post = posts.find((p) => p.id === editingBlogId);
    const { error } = await updateNewsPost(editingBlogId, {
      title: blogTitle,
      content: blogContent,
      image_urls: post?.image_urls ?? [],
    });
    if (error) { setBlogSubmitError(error.message); setBlogSubmitting(false); return; }
    setNotice('Innlegget er oppdatert.');
    resetBlogEdit();
    await loadPosts();
    setBlogSubmitting(false);
  };

  const resetNewsForm = () => {
    setShowNewsForm(false);
    setEditingNewsId(null);
    setNewsTitle('');
    setNewsContent('');
    setNewsAuthor('');
    setNewsImageFile(null);
    setNewsImagePreview((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    setExistingNewsImageUrl(null);
    setNewsSubmitError('');
    if (newsFileRef.current) newsFileRef.current.value = '';
  };

  const startEditNews = (article: NewsArticle) => {
    setShowNewsForm(false);
    setEditingNewsId(article.id);
    setNewsTitle(article.title);
    setNewsContent(article.content);
    setNewsAuthor(article.author ?? '');
    setExistingNewsImageUrl(article.image_url);
    setNewsImageFile(null);
    setNewsImagePreview((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    setNewsSubmitError('');
  };

  const handleNewsImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setNewsImagePreview((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    setNewsImageFile(file);
    setNewsImagePreview(URL.createObjectURL(file));
    if (newsFileRef.current) newsFileRef.current.value = '';
  };

  const handleNewsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNewsSubmitError('');
    setNewsSubmitting(true);

    let imageUrl = existingNewsImageUrl ?? null;

    if (newsImageFile) {
      const { url, error: uploadErr } = await uploadNewsArticleImage(newsImageFile);
      if (uploadErr || !url) {
        setNewsSubmitError(uploadErr ?? 'Bildeopplasting feilet.');
        setNewsSubmitting(false);
        return;
      }
      imageUrl = url;
    }

    if (editingNewsId) {
      const { error } = await updateNewsArticle(editingNewsId, {
        title: newsTitle,
        content: newsContent,
        image_url: imageUrl,
        author: newsAuthor.trim() || null,
      });
      if (error) { setNewsSubmitError(error.message); setNewsSubmitting(false); return; }
      setNotice('Saken er oppdatert.');
    } else {
      const { error } = await createNewsArticle({
        title: newsTitle,
        content: newsContent,
        image_url: imageUrl,
        author: newsAuthor.trim() || null,
      });
      if (error) { setNewsSubmitError(error.message); setNewsSubmitting(false); return; }
      setNotice('Saken er publisert.');
    }

    resetNewsForm();
    await loadNewsArticles();
    setNewsSubmitting(false);
  };

  const handleDeleteNews = async (id: string) => {
    if (!window.confirm('Slett denne nyheten permanent?')) return;
    setActionLoading(id + '_news');
    await deleteNewsArticle(id);
    setNotice('Nyheten er slettet.');
    await loadNewsArticles();
    setActionLoading(null);
  };

  // ── Tab counts ─────────────────────────────────────────────────────────────
  const pendingCount = requests.filter((r) => r.status === 'pending').length;

  const navigate = useNavigate();

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#080808] text-white py-12 relative overflow-hidden">
      <div className="absolute -top-24 right-0 w-64 h-64 pointer-events-none" />

      <div className="container mx-auto px-4 max-w-5xl relative z-10">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-5xl font-bold text-white tracking-tight">Admin</h1>
          <p className="text-gray-400 mt-1">Administrer brukere, roller og innhold.</p>
        </div>

        {/* Notice */}
        {notice && (
          <div className="mb-6 bg-emerald-900 bg-opacity-30 border border-emerald-500 border-opacity-40 text-emerald-200 px-4 py-3 rounded-xl text-sm flex justify-between">
            <span>{notice}</span>
            <button onClick={() => setNotice('')} className="text-gray-500 hover:text-white ml-4">&times;</button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-8" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          {([
            { key: 'requests' as Tab, label: 'Søknader', count: pendingCount },
            { key: 'users' as Tab, label: 'Brukere' },
            { key: 'posts' as Tab, label: 'Innlegg' },
            { key: 'news' as Tab, label: 'Nyheter' },
          ] as { key: Tab; label: string; count?: number }[]).map(({ key, label, count }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`pb-3 px-4 text-sm font-bold transition-all relative ${
                activeTab === key
                  ? 'text-white border-b-2 border-white'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {label}
              {count !== undefined && count > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 text-xs bg-white text-black rounded-full font-bold">
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── REQUESTS TAB ──────────────────────────────────────────────── */}
        {activeTab === 'requests' && (
          <section className="space-y-4">
            {requestsLoading ? (
              <p className="text-gray-500 animate-pulse">Laster søknader...</p>
            ) : requests.length === 0 ? (
              <p className="text-gray-500">Ingen søknader enda.</p>
            ) : (
              requests.map((req) => (
                <div
                  key={req.id}
                  className="p-5 flex flex-col md:flex-row md:items-start gap-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  {/* Avatar + name */}
                  <div className="flex items-center gap-3 shrink-0">
                    {req.profiles.avatar_url ? (
                      <img
                        src={req.profiles.avatar_url}
                        alt={req.profiles.username}
                        className="w-10 h-10 rounded-full object-cover shrink-0" style={{ border: '1px solid rgba(255,255,255,0.1)' }}
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.3)' }}>?</div>
                    )}
                    <div>
                      <p className="font-semibold text-white">@{req.profiles.username}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(req.created_at).toLocaleDateString('nb-NO', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </p>
                    </div>
                  </div>

                  {/* Message + status */}
                  <div className="flex-1 space-y-2">
                    {req.message && (
                      <p className="text-gray-300 text-sm italic">
                        &ldquo;{req.message}&rdquo;
                      </p>
                    )}
                    <span
                      className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full ${
                        req.status === 'pending'
                          ? 'bg-yellow-950 text-yellow-300'
                          : req.status === 'approved'
                          ? 'bg-emerald-950 text-emerald-300'
                          : 'bg-red-950 text-red-300'
                      }`}
                    >
                      {req.status === 'pending' ? 'Venter' : req.status === 'approved' ? 'Godkjent' : 'Avslått'}
                    </span>
                  </div>

                  {/* Actions */}
                  {req.status === 'pending' && (
                    <div className="flex gap-2 shrink-0">
                      <button
                        onClick={() => handleApprove(req)}
                        disabled={actionLoading !== null}
                        className="btn-primary py-2 px-4 text-sm disabled:opacity-50"
                      >
                        {actionLoading === req.id ? '...' : 'Godkjenn'}
                      </button>
                      <button
                        onClick={() => handleDeny(req)}
                        disabled={actionLoading !== null}
                        className="btn-secondary py-2 px-4 text-sm disabled:opacity-50"
                      >
                        {actionLoading === req.id + '_deny' ? '...' : 'Avslå'}
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </section>
        )}

        {/* ── USERS TAB ─────────────────────────────────────────────────── */}
        {activeTab === 'users' && (
          <section className="space-y-3">
            {roleError && (
              <div className="px-4 py-2 rounded text-sm text-red-300" style={{ background: 'rgba(200,0,0,0.15)', border: '1px solid rgba(200,0,0,0.3)' }}>
                {roleError}
              </div>
            )}
            {usersLoading ? (
              <p className="text-gray-500 animate-pulse">Laster brukere...</p>
            ) : profiles.length === 0 ? (
              <p className="text-gray-500">Ingen brukere funnet.</p>
            ) : (
              profiles.map((profile) => {
                const isLoadingThis = actionLoading === profile.user_id;
                const isSelf = profile.user_id === user?.id;
                return (
                  <div
                    key={profile.user_id}
                    className="px-5 py-4 flex items-center gap-4 flex-wrap" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    {/* Avatar */}
                    {profile.avatar_url ? (
                      <img
                        src={profile.avatar_url}
                        alt={profile.username}
                        className="w-9 h-9 rounded-full object-cover shrink-0" style={{ border: '1px solid rgba(255,255,255,0.1)' }}
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.3)' }}>?</div>
                    )}

                    {/* Name */}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-white truncate">@{profile.username}</p>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-semibold ${ROLE_COLORS[profile.role] ?? ROLE_COLORS.user}`}
                      >
                        {ROLE_LABELS[profile.role] ?? profile.role}
                      </span>
                    </div>

                    {/* Role buttons — don't let admin change their own role */}
                    {!isSelf ? (
                      <div className="flex items-center gap-1 shrink-0">
                        {isLoadingThis && (
                          <span className="text-xs text-gray-500 mr-1 animate-pulse">Lagrer…</span>
                        )}
                        {(['user', 'smugler', 'admin'] as UserRole[]).map((role) => {
                          const isActive = profile.role === role;
                          const activeStyle: Record<UserRole, React.CSSProperties> = {
                            user: { background: 'rgba(75,85,99,0.5)', borderColor: 'rgba(156,163,175,0.5)', color: '#d1d5db' },
                            smugler: { background: 'rgba(6,78,59,0.6)', borderColor: 'rgba(52,211,153,0.5)', color: '#6ee7b7' },
                            admin: { background: 'rgba(113,63,18,0.6)', borderColor: 'rgba(253,224,71,0.5)', color: '#fde047' },
                          };
                          const inactiveStyle: React.CSSProperties = {
                            background: 'rgba(255,255,255,0.04)',
                            borderColor: 'rgba(255,255,255,0.08)',
                            color: 'rgba(255,255,255,0.3)',
                          };
                          return (
                            <button
                              key={role}
                              type="button"
                              disabled={isLoadingThis || actionLoading !== null}
                              onClick={() => handleRoleChange(profile, role)}
                              style={isActive ? activeStyle[role] : inactiveStyle}
                              className="text-xs font-semibold px-3 py-1.5 rounded border transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90"
                            >
                              {ROLE_LABELS[role]}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-600 shrink-0">Deg selv</span>
                    )}
                  </div>
                );
              })
            )}
          </section>
        )}

        {/* ── POSTS TAB ─────────────────────────────────────────────────── */}
        {activeTab === 'posts' && (
          <section className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-500">{posts.length} innlegg</p>
            </div>
            {postsLoading ? (
              <p className="text-gray-500 animate-pulse">Laster innlegg…</p>
            ) : posts.length === 0 ? (
              <p className="text-gray-500">Ingen innlegg funnet.</p>
            ) : (
              posts.map((post) => (
                <div key={post.id}>
                  {/* Inline edit form */}
                  {editingBlogId === post.id ? (
                    <form
                      onSubmit={handleBlogEditSubmit}
                      className="p-5 space-y-4"
                      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)' }}
                    >
                      <h3 className="text-sm font-bold text-white">Rediger innlegg</h3>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Tittel</label>
                        <input
                          type="text"
                          value={blogTitle}
                          onChange={(e) => setBlogTitle(e.target.value)}
                          required
                          maxLength={200}
                          className="input-glow w-full text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Innhold</label>
                        <textarea
                          value={blogContent}
                          onChange={(e) => setBlogContent(e.target.value)}
                          required
                          maxLength={10000}
                          rows={6}
                          className="input-glow w-full resize-y min-h-[120px] text-sm"
                        />
                      </div>
                      {blogSubmitError && <p className="text-red-400 text-xs">{blogSubmitError}</p>}
                      <div className="flex gap-3">
                        <button type="submit" disabled={blogSubmitting} className="btn-primary py-1.5 px-5 text-sm disabled:opacity-50">
                          {blogSubmitting ? 'Lagrer…' : 'Lagre'}
                        </button>
                        <button type="button" onClick={resetBlogEdit} className="btn-secondary py-1.5 px-5 text-sm">
                          Avbryt
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div
                      className="px-5 py-4 flex items-start gap-4"
                      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                    >
                      {/* Thumbnail */}
                      {post.image_urls[0] ? (
                        <img
                          src={post.image_urls[0]}
                          alt={post.title}
                          className="w-16 h-16 object-cover shrink-0 rounded"
                          style={{ border: '1px solid rgba(255,255,255,0.08)' }}
                        />
                      ) : (
                        <div
                          className="w-16 h-16 shrink-0 rounded flex items-center justify-center text-xs"
                          style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.2)' }}
                        >
                          Ingen
                        </div>
                      )}

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-white truncate mb-0.5">{post.title}</p>
                        <p className="text-xs text-gray-500">
                          {post.author_display_name && (
                            <span className="text-gray-400 font-medium">@{post.author_display_name} · </span>
                          )}
                          {new Date(post.created_at).toLocaleDateString('nb-NO', {
                            day: 'numeric', month: 'long', year: 'numeric',
                          })}
                        </p>
                        <p className="text-xs mt-1 line-clamp-2" style={{ color: 'rgba(255,255,255,0.35)' }}>
                          {post.content.slice(0, 120)}{post.content.length > 120 && '…'}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 shrink-0 items-start flex-wrap justify-end">
                        <button
                          type="button"
                          onClick={() => navigate(`/blog/${post.id}`)}
                          className="btn-secondary py-1.5 px-3 text-xs"
                        >
                          Vis
                        </button>
                        <button
                          type="button"
                          onClick={() => startEditBlog(post)}
                          disabled={actionLoading !== null}
                          className="btn-secondary py-1.5 px-3 text-xs disabled:opacity-50"
                        >
                          Rediger
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeletePost(post.id)}
                          disabled={actionLoading !== null}
                          className="text-xs text-red-400 hover:text-red-300 border border-red-700 border-opacity-50 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {actionLoading === post.id ? '…' : 'Slett'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </section>
        )}

        {/* ── NEWS TAB ──────────────────────────────────────────────────── */}
        {activeTab === 'news' && (
          <section className="space-y-6">
            {/* Toolbar */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">{newsArticles.length} saker</p>
              {!showNewsForm && !editingNewsId && (
                <button
                  type="button"
                  onClick={() => { resetNewsForm(); setShowNewsForm(true); }}
                  className="btn-primary py-2 px-5 text-sm font-bold"
                >
                  + Ny sak
                </button>
              )}
            </div>

            {/* Create / edit form */}
            {(showNewsForm || editingNewsId) && (
              <form
                onSubmit={handleNewsSubmit}
                className="card-glow p-6 space-y-4"
              >
                <h2 className="text-lg font-bold text-white">
                  {editingNewsId ? 'Rediger sak' : 'Ny sak'}
                </h2>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Tittel</label>
                  <input
                    type="text"
                    value={newsTitle}
                    onChange={(e) => setNewsTitle(e.target.value)}
                    required
                    maxLength={200}
                    placeholder="Overskrift…"
                    className="input-glow w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Byline / forfatter (valgfritt)</label>
                  <input
                    type="text"
                    value={newsAuthor}
                    onChange={(e) => setNewsAuthor(e.target.value)}
                    maxLength={100}
                    placeholder="f.eks. Smuglergods redaksjon"
                    className="input-glow w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Innhold</label>
                  <textarea
                    value={newsContent}
                    onChange={(e) => setNewsContent(e.target.value)}
                    required
                    maxLength={10000}
                    rows={6}
                    placeholder="Skriv saken her…"
                    className="input-glow w-full resize-y min-h-[120px]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Bilde (valgfritt)</label>
                  <input
                    ref={newsFileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleNewsImageSelect}
                    className="hidden"
                  />
                  <div className="flex flex-wrap gap-4 items-start">
                    <button
                      type="button"
                      onClick={() => newsFileRef.current?.click()}
                      className="btn-secondary py-2 px-4 text-sm"
                    >
                      {existingNewsImageUrl || newsImagePreview ? 'Bytt bilde' : 'Legg til bilde'}
                    </button>
                    {(newsImagePreview ?? existingNewsImageUrl) && (
                      <div className="relative">
                        <img
                          src={newsImagePreview ?? existingNewsImageUrl!}
                          alt="Forhåndsvisning"
                          className="h-24 w-auto object-cover"
                          style={{ border: '1px solid rgba(255,255,255,0.15)' }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setNewsImageFile(null);
                            setNewsImagePreview((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
                            setExistingNewsImageUrl(null);
                          }}
                          className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center rounded-full bg-red-600 text-white text-sm hover:bg-red-500"
                          aria-label="Fjern bilde"
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {newsSubmitError && (
                  <p className="text-red-400 text-sm">{newsSubmitError}</p>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={newsSubmitting}
                    className="btn-primary py-2 px-6 disabled:opacity-50"
                  >
                    {newsSubmitting
                      ? (editingNewsId ? 'Oppdaterer…' : 'Publiserer…')
                      : (editingNewsId ? 'Oppdater' : 'Publiser')}
                  </button>
                  <button
                    type="button"
                    onClick={resetNewsForm}
                    className="btn-secondary py-2 px-6"
                  >
                    Avbryt
                  </button>
                </div>
              </form>
            )}

            {/* List */}
            {newsLoading ? (
              <p className="text-gray-500 animate-pulse">Laster nyheter…</p>
            ) : newsArticles.length === 0 && !showNewsForm && !editingNewsId ? (
              <p className="text-gray-500">Ingen saker publisert ennå.</p>
            ) : (
              newsArticles.map((article) => (
                <div
                  key={article.id}
                  className="px-5 py-4 flex items-center gap-4"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  {article.image_url && (
                    <img
                      src={article.image_url}
                      alt={article.title}
                      className="w-14 h-14 object-cover shrink-0"
                      style={{ border: '1px solid rgba(255,255,255,0.1)' }}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-white truncate">{article.title}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(article.created_at).toLocaleDateString('nb-NO', {
                        day: 'numeric', month: 'long', year: 'numeric',
                      })}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => startEditNews(article)}
                      disabled={actionLoading !== null}
                      className="btn-secondary py-1.5 px-3 text-sm disabled:opacity-50"
                    >
                      Rediger
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteNews(article.id)}
                      disabled={actionLoading !== null}
                      className="text-sm text-red-400 hover:text-red-300 border border-red-700 border-opacity-50 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {actionLoading === article.id + '_news' ? '…' : 'Slett'}
                    </button>
                  </div>
                </div>
              ))
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default Admin;
